/** Adds "Dr." before HCP display names; skips if already prefixed (case-insensitive). */
import {
  ALL_PRODUCT_GROUP_OPTION,
  PRODUCT_SALES_GROUP_MAP,
} from "../../utils/constantFiles/constant";

export function normalizeProductGroupKey(value) {
  return (value ?? "").toString().trim().toUpperCase();
}

export function isAllProductGroupSelection(selectedProduct) {
  return normalizeProductGroupKey(selectedProduct) === "ALL";
}

export function isTyvasoProductSelection(selectedProduct) {
  return normalizeProductGroupKey(selectedProduct) === "TYVASO";
}

export function productGroupKeysMatch(selectedKey, rowKey) {
  const key = normalizeProductGroupKey(selectedKey);
  const pg = normalizeProductGroupKey(rowKey);
  if (!key || !pg) return false;
  if (pg === key) return true;
  if (key === "TYVASO" && pg.startsWith("TYVASO")) return true;
  if (pg === "TYVASO" && key.startsWith("TYVASO")) return true;
  return false;
}

/** Prepends "All" when a rep is assigned two or more product groups. */
export function buildProductGroupFilterOptions(productGroups = []) {
  const groups = [...new Set((productGroups || []).filter(Boolean))];
  if (groups.length >= 2) {
    return [ALL_PRODUCT_GROUP_OPTION, ...groups];
  }
  return groups;
}

export function findProductGroupDictEntry(dict, productKey) {
  if (!dict || productKey == null || productKey === "") return null;
  if (dict[productKey] != null) return dict[productKey];

  const match = Object.entries(dict).find(([key]) =>
    productGroupKeysMatch(productKey, key),
  );
  return match?.[1] ?? null;
}

/** Resolves My Performance card metrics from API `productgroup` (including `ALL`). */
export function getSalesRepProductMetricsFromApi(productgroupDict, selectedProduct) {
  return findProductGroupDictEntry(productgroupDict, selectedProduct);
}

export function getSalesRepPerformanceRowsByProduct(performanceByProduct, selectedProduct) {
  const fromApi = findProductGroupDictEntry(performanceByProduct, selectedProduct);
  return Array.isArray(fromApi) ? fromApi : [];
}

export function getSalesRepHcpGrowers(hcpGrowersDict, selectedProduct) {
  const block = findProductGroupDictEntry(hcpGrowersDict, selectedProduct);
  return block || { Increase: [], Decrease: [] };
}

export function getPendingReferralsTotal(totalsByProduct, selectedProduct) {
  const fromApi = findProductGroupDictEntry(totalsByProduct, selectedProduct);
  if (fromApi === null || fromApi === undefined) return 0;
  return Number(fromApi) || 0;
}

/** Pending referral age-bucket rows from API `productgroup.ALL` or product-specific rows. */
export function getPendingReferralsDetailRows(detailRows, selectedProduct) {
  const all = Array.isArray(detailRows) ? detailRows : [];
  if (!selectedProduct) return [];

  const pg = (item) => item.productgroup ?? item.ProductGroup;

  if (isAllProductGroupSelection(selectedProduct)) {
    return all.filter((item) => productGroupKeysMatch("ALL", pg(item)));
  }

  return all.filter(
    (item) =>
      item.metric === "Pending referrals" &&
      productGroupKeysMatch(selectedProduct, pg(item)),
  );
}

/** HCP pending referral rows from API `productgroup.ALL` or product-specific rows. */
export function getPendingReferralsHcpRows(hcpRows, selectedProduct) {
  const list = Array.isArray(hcpRows) ? hcpRows : [];
  if (!selectedProduct) return [];

  const pg = (item) => item.productgroup ?? item.ProductGroup;
  const filterKey = isAllProductGroupSelection(selectedProduct)
    ? "ALL"
    : selectedProduct;

  return list.filter((item) => productGroupKeysMatch(filterKey, pg(item)));
}

export function withDoctorNamePrefix(rawName) {
  const name = (rawName ?? "").toString().trim();
  if (!name) return "";
  const lower = name.toLowerCase();
  if (lower.startsWith("dr.") || lower.startsWith("dr ")) return name;
  return `Dr. ${name}`;
}

export const splitFullName = (full = "") => {
  const [f = "", l = ""] = full.trim().split(/\s+/, 2);
  return [f, l];
};

export const capitalizeWord = (s = "") =>
  s ? s.trim().charAt(0).toUpperCase() + s.trim().slice(1).toLowerCase() : "";

export const getSelectedMedicineLeadership = (effectiveSelectedProduct) => {
  if (!effectiveSelectedProduct) return "";
  const mapped = PRODUCT_SALES_GROUP_MAP[effectiveSelectedProduct];
  if (mapped?.product) {
    const product = mapped.product;
    return product.charAt(0) + product.slice(1).toLowerCase();
  }
  return effectiveSelectedProduct;
};

export const getLeadershipSalesGroup = (effectiveSelectedProduct) => {
  if (!effectiveSelectedProduct) return "";
  const mapped = PRODUCT_SALES_GROUP_MAP[effectiveSelectedProduct];
  if (mapped?.salesGroup) return mapped.salesGroup;
  return effectiveSelectedProduct;
};

/** Resolves the dashboard product filter to sales group(s) for account-change `Group` matching. */
export function getSalesGroupsForSelectedProduct(selectedProduct) {
  const key = (selectedProduct ?? "").toString().trim();
  if (!key || key.toUpperCase() === "ALL") return [];

  const direct = PRODUCT_SALES_GROUP_MAP[key];
  if (direct?.salesGroup) {
    return [direct.salesGroup.toUpperCase()];
  }

  const keyUpper = key.toUpperCase();
  const fromProduct = Object.values(PRODUCT_SALES_GROUP_MAP)
    .filter((entry) => {
      const product = (entry.product ?? "").toUpperCase();
      if (product === keyUpper) return true;
      if (keyUpper.startsWith("TYVASO") && product === "TYVASO") return true;
      return false;
    })
    .map((entry) => entry.salesGroup.toUpperCase());

  if (fromProduct.length) {
    return [...new Set(fromProduct)];
  }

  return [keyUpper];
}

/** Matches `/kpi_metrics/hcp_hco_account_changes` rows where `Group` aligns with dashboard filter. */
export function accountChangeRowMatchesSelectedProduct(row, selectedProduct) {
  const groups = getSalesGroupsForSelectedProduct(selectedProduct);
  if (!groups.length) return true;

  const rowGroup = (row?.Group ?? row?.group ?? "").toString().trim().toUpperCase();
  if (!rowGroup) return false;

  return groups.includes(rowGroup);
}

/** Matches `/kpi_metrics/insights` rows where ProductGroup aligns with My Performance product selection. */
export function insightRowMatchesSelectedProduct(
  row,
  selectedProduct,
  assignedProductGroups = null,
) {
  const key = (selectedProduct ?? "").toString().trim().toUpperCase();
  if (!key) return true;

  const pg = (row?.ProductGroup ?? row?.productgroup ?? "").toString().trim();
  if (!pg) return false;

  if (key === "ALL") {
    if (productGroupKeysMatch("ALL", pg)) return true;
    if (!Array.isArray(assignedProductGroups) || assignedProductGroups.length === 0) {
      return false;
    }
    return assignedProductGroups.some((assigned) =>
      productGroupKeysMatch(assigned, pg),
    );
  }

  return productGroupKeysMatch(selectedProduct, pg);
}

/** Mirrors API `COUNT(DISTINCT Details)` for a filtered row list. */
export function distinctInsightDetailsCount(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return 0;
  const set = new Set();
  for (const r of rows) {
    const d = String(r?.Details ?? "").trim();
    if (d) set.add(d);
  }
  return set.size;
}

/** Extracts HCP name from de novo writer description text. */
export function extractHcpNameFromDeNovoDescription(description) {
  const text = (description ?? "").toString().trim();
  if (!text) return "";

  const referredMatch = text.match(/Dr\.?\s+(.+?)\s+referred\b/i);
  if (referredMatch?.[1]) return referredMatch[1].trim();

  const drMatch = text.match(/Dr\.?\s+([A-Za-z][A-Za-z\s'.-]+)/i);
  if (drMatch?.[1]) return drMatch[1].trim();

  return "";
}

export const buildLeadershipAlertPrompt = (alert) => {
  if (!alert) return "";

  const message = (alert.raw?.AlertMessage || "").toLowerCase();
  const productGroup = alert.product;
  const region = alert.region || "this region";
  const metric = alert.raw?.metric || "";
  const salesGroup = alert.raw?.SALES_GROUP || "";

  const kpiLabel = metric || "this KPI";

  if (message.includes("benchmark")) {
    return `Summarize the performance of ${salesGroup} in ${region} this quarter`;
  }
  if (message.startsWith("reach")) {
    return `How is the ${salesGroup} group tracking towards the reach and frequency goals?`;
  }
  if (
    message.includes("not on track") &&
    message.includes("start goal this quarter")
  ) {
    return `Summarize the performance of ${productGroup} in ${region}`;
  }
  if (message.includes("are up by") && message.includes("compared to")) {
    return `What territories are contributing to the increase in ${kpiLabel} for ${productGroup} in ${region} for ${salesGroup}?`;
  }
  if (message.includes("are down by") && message.includes("compared to")) {
    return `What territories are contributing to the decrease in ${kpiLabel} for ${productGroup} in ${region} for ${salesGroup}?`;
  }

  return `Summarize the key performance drivers for ${productGroup} in ${region}`;
};
